import { useState, type FormEvent } from 'react';
import { useApp } from '../../lib/app';
import { supabase } from '../../lib/supabase';
import { useRows } from '../../lib/useRows';
import { api, write } from '../../lib/api';
import { Badge, Button, Card, Empty, Field, errText, useToast } from '../../ui/kit';
import { C3PageLoading, C3Busy } from '../../ui/C3Loader';

type P = { id: string; email: string | null; full_name: string | null; org_id: string | null; created_at: string };
type UR = { id: string; user_id: string; role_key: string; site_id: string | null; deleted_at: string | null };
type R = { key: string; name: string; internal: boolean; sort: number };

export default function Users() {
  const { access, sites, siteId, reload: reloadApp } = useApp();
  const toast = useToast();
  const isSuper = Boolean(access?.super_admin);
  const profiles = useRows<P>(() => supabase.from('profiles').select('id, email, full_name, org_id, created_at').order('full_name'), [], ['profiles']);
  const orgs = useRows<{ id: string; name: string }>(() => supabase.from('organisations').select('id, name').is('deleted_at', null).order('name'), [], ['organisations']);
  const userRoles = useRows<UR>(() => supabase.from('user_roles').select('id, user_id, role_key, site_id, deleted_at').is('deleted_at', null), [], ['user_roles']);
  const roles = useRows<R>(() => supabase.from('roles').select('key, name, internal, sort').is('deleted_at', null).order('sort'), []);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('pm');
  const [scope, setScope] = useState<string>(siteId ?? '');
  const [busy, setBusy] = useState(false);
  const [addFor, setAddFor] = useState<string | null>(null);
  const [addRole, setAddRole] = useState('pm');
  const [addScope, setAddScope] = useState<string>(siteId ?? '');

  const siteName = (id: string | null) => (id ? sites.find((s) => s.id === id)?.short_code ?? 'site' : 'All sites');
  const roleName = (k: string) => roles.rows.find((r) => r.key === k)?.name ?? k;

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api<{ invited: boolean }>('invite', { email, full_name: name || undefined, role_key: role, site_id: scope || null });
      toast({ tone: 'ok', text: r.invited ? `Invite sent to ${email}` : `${email} already had an account — role added` });
      setEmail('');
      setName('');
      void profiles.reload();
      void userRoles.reload();
    } catch (err) {
      toast({ tone: 'error', text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  const [mailBusy, setMailBusy] = useState(false);
  async function testEmail() {
    setMailBusy(true);
    try {
      const r = await api<{ to: string }>('test-email', {});
      toast({ tone: 'ok', text: `Test email sent to ${r.to} — check your inbox` });
    } catch (err) {
      toast({ tone: 'error', text: errText(err) });
    } finally {
      setMailBusy(false);
    }
  }

  async function addRoleTo(uid: string) {
    try {
      await write('user_roles', 'insert', { values: { user_id: uid, role_key: addRole, site_id: addScope || null }, summary: `Gave role ${addRole}` });
      setAddFor(null);
      void userRoles.reload();
      void reloadApp();
    } catch (err) {
      toast({ tone: 'error', text: errText(err) });
    }
  }

  async function removeRole(ur: UR, who: string) {
    if (!window.confirm(`Remove ${roleName(ur.role_key)} (${siteName(ur.site_id)}) from ${who}?`)) return;
    try {
      await write('user_roles', 'delete', { id: ur.id, summary: `Removed role ${ur.role_key} from ${who}` });
      void userRoles.reload();
      void reloadApp();
    } catch (err) {
      toast({ tone: 'error', text: errText(err) });
    }
  }

  const roleOptions = roles.rows.map((r) => (
    <option key={r.key} value={r.key}>
      {r.name}
      {r.internal ? '' : ' (outside user)'}
    </option>
  ));
  const scopeOptions = (
    <>
      <option value="">All sites</option>
      {sites.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </>
  );

  return (
    <>
      {isSuper ? (
        <Card title="Invite a person">
          <form className="form" onSubmit={invite}>
            <div className="form-row">
              <Field label="Email *">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              <Field label="Name">
                <input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Role">
                <select value={role} onChange={(e) => setRole(e.target.value)}>{roleOptions}</select>
              </Field>
              <Field label="For">
                <select value={scope} onChange={(e) => setScope(e.target.value)}>{scopeOptions}</select>
              </Field>
            </div>
            <div className="toolbar">
              <Button kind="primary" type="submit" disabled={busy}>
                {busy ? <C3Busy text="Sending…" accent /> : 'Send invite'}
              </Button>
              <span className="muted small">They get an email with a 6-digit code and a link to pm.skywardindustries.com/welcome.</span>
              <span style={{ flex: 1 }} />
              <Button onClick={() => void testEmail()} disabled={mailBusy}>
                {mailBusy ? <C3Busy text="Sending…" /> : 'Send me a test email'}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card title="People" pad={false}>
        {profiles.loading ? (
          <C3PageLoading label="Loading users" />
        ) : profiles.rows.length === 0 ? (
          <Empty>No one yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Company</th>
                  <th>Roles</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {profiles.rows.map((p) => {
                  const mine = userRoles.rows.filter((u) => u.user_id === p.id);
                  const who = p.full_name || p.email || 'user';
                  return (
                    <tr key={p.id}>
                      <td>
                        <strong>{p.full_name || '—'}</strong>
                        <div className="muted small">{p.email}</div>
                      </td>
                      <td>
                        {isSuper ? (
                          <select
                            aria-label={`Company of ${who}`}
                            value={p.org_id ?? ''}
                            title="An architect of record signs certificates for their company"
                            onChange={(e) =>
                              void write('profiles', 'update', { id: p.id, values: { org_id: e.target.value || null }, summary: `Company of ${who}` })
                                .then(() => profiles.reload())
                                .catch((err) => toast({ tone: 'error', text: errText(err) }))
                            }
                          >
                            <option value="">—</option>
                            {orgs.rows.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="small">{orgs.rows.find((o) => o.id === p.org_id)?.name ?? '—'}</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {mine.length === 0 ? <span className="muted small">No role</span> : null}
                          {mine.map((u) => (
                            <span key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Badge tone={u.role_key === 'super_admin' ? 'orange' : 'neutral'}>
                                {roleName(u.role_key)} · {siteName(u.site_id)}
                              </Badge>
                              {isSuper ? (
                                <button type="button" className="btn-ghost btn" style={{ minHeight: 24, padding: '0 4px' }} aria-label={`Remove ${roleName(u.role_key)}`} onClick={() => void removeRole(u, who)}>
                                  ×
                                </button>
                              ) : null}
                            </span>
                          ))}
                        </div>
                        {addFor === p.id ? (
                          <div className="toolbar" style={{ marginTop: 8 }}>
                            <select value={addRole} onChange={(e) => setAddRole(e.target.value)} aria-label="Role">{roleOptions}</select>
                            <select value={addScope} onChange={(e) => setAddScope(e.target.value)} aria-label="For">{scopeOptions}</select>
                            <Button kind="primary" onClick={() => void addRoleTo(p.id)}>Add</Button>
                            <Button onClick={() => setAddFor(null)}>Cancel</Button>
                          </div>
                        ) : null}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isSuper && addFor !== p.id ? (
                          <Button kind="ghost" onClick={() => setAddFor(p.id)}>
                            + Role
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
